import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useNavigate, Link } from 'react-router-dom';
import { DEVELLO_API_ORIGIN } from '../utils/asyncReliability';
import LogoImg from '../ChatGPT Image Jun 26, 2025, 12_35_25 AM.png';

const Logo = () => (
  <span className="flex items-center justify-center mb-6">
    <img
      src={LogoImg}
      alt="DevelloTech Logo"
      style={{ width: 96, height: 96, objectFit: 'contain', display: 'block' }}
    />
    <span
      className="text-3xl font-bold tracking-tight"
      style={{
        background: 'linear-gradient(90deg, #1761A0 0%, #2ED8C3 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent',
        marginLeft: -12,
      }}
    >
      DevelloTech
    </span>
  </span>
);

function getFriendlyError(error) {
  if (!error) return 'An unexpected error occurred.';
  const msg = error.message || error.error_description || '';
  if (msg.includes('Invalid login')) return 'Invalid email or password.';
  if (msg.includes('rate limit') || msg.includes('too many')) return 'Too many attempts. Please try again later.';
  return msg || 'An unexpected error occurred.';
}

// ── Shared input/button styles ─────────────────────────────────
const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm';
const btnCls   = 'w-full bg-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50';

// ── Recovery-mode: set new password after clicking reset link ──
function RecoveryView({ onDone }) {
  const [newPw, setNewPw]       = useState('');
  const [confirm, setConfirm]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');
  const [ok, setOk]             = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { setErr('Password must be at least 6 characters.'); return; }
    if (newPw !== confirm) { setErr('Passwords do not match.'); return; }
    setSaving(true); setErr('');
    const { error } = await supabase.auth.updateUser({ password: newPw });
    if (error) { setErr(error.message); setSaving(false); return; }
    setOk(true);
    await supabase.auth.signOut();
    setTimeout(onDone, 2000);
  };

  return (
    <div>
      <div className="text-center mb-6">
        <Logo />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Set New Password</h2>
        <p className="text-gray-600 text-sm">Enter a new password for your account.</p>
      </div>
      {ok ? (
        <p className="text-center text-emerald-700 font-medium py-4">Password updated! Redirecting to sign in…</p>
      ) : (
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className={inputCls} placeholder="••••••••" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} placeholder="••••••••" required />
          </div>
          {err && <p className="text-red-600 text-sm text-center">{err}</p>}
          <button type="submit" disabled={saving} className={btnCls}>{saving ? 'Saving…' : 'Set Password'}</button>
        </form>
      )}
    </div>
  );
}

// ── Forgot-password sub-view ───────────────────────────────────
function ForgotView({ onBack }) {
  const [email, setEmail]   = useState('');
  const [busy, setBusy]     = useState(false);
  const [msg, setMsg]       = useState('');
  const [isErr, setIsErr]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    setBusy(true); setMsg(''); setIsErr(false);
    try {
      // Ask server if this email belongs to an active team member
      const resp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error || 'Something went wrong.');

      if (json.canReset) {
        // Trigger Supabase's built-in reset email
        await supabase.auth.resetPasswordForEmail(trimmed, {
          redirectTo: window.location.origin + '/team-login',
        });
        setMsg('Check your email for a password reset link.');
      } else {
        // Active member not found — give a neutral message for security
        setMsg('If that email belongs to an active account, a reset link has been sent.');
      }
    } catch (err) {
      setIsErr(true);
      setMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-6">
        <Logo />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Forgot Password</h2>
        <p className="text-gray-600 text-sm">Enter your email and we'll send a reset link.</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="Enter your email" required />
        </div>
        {msg && <p className={`text-sm text-center ${isErr ? 'text-red-600' : 'text-emerald-700'}`}>{msg}</p>}
        <button type="submit" disabled={busy} className={btnCls}>{busy ? 'Sending…' : 'Send Reset Link'}</button>
      </form>
      <div className="mt-4 text-center">
        <button type="button" onClick={onBack} className="text-sm text-primary hover:text-primary-dark font-medium">← Back to Sign In</button>
      </div>
    </div>
  );
}

// ── Main sign-in view ──────────────────────────────────────────
const TeamMemberLogin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [mode, setMode]       = useState('login'); // 'login' | 'forgot' | 'recovery'
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ email: '', password: '' });

  // Detect Supabase password-recovery session (user clicked reset link)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('type=recovery')) {
      setMode('recovery');
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setMode('recovery');
    });
    return () => subscription.unsubscribe();
  }, []);

  // Already signed in as a team member — go straight to the dashboard.
  useEffect(() => {
    if (mode !== 'login') return;
    let cancelled = false;

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled || !session?.user) return;
      if (session.user.app_metadata?.user_type !== 'team_member') return;

      try {
        const resp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/me`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        await resp.json();
        if (!resp.ok) return;
        if (!cancelled) navigate('/team-dashboard', { replace: true });
      } catch {
        /* stay on login */
      }
    });

    return () => { cancelled = true; };
  }, [mode, navigate]);

  const handleInputChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });
      if (signInError) throw signInError;

      const userType = data.user?.app_metadata?.user_type;
      if (userType !== 'team_member') {
        await supabase.auth.signOut();
        setError('This account belongs to a contractor. Please use the contractor sign-in page.');
        return;
      }

      const token = data.session?.access_token;
      const resp = await fetch(`${DEVELLO_API_ORIGIN}/api/team/me`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await resp.json();

      if (!resp.ok) {
        await supabase.auth.signOut();
        setError(json.error || 'Account not found or deactivated.');
        return;
      }

      navigate('/team-dashboard');
    } catch (err) {
      setError(getFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl p-8 shadow-strong">
          {mode === 'recovery' && <RecoveryView onDone={() => { setMode('login'); window.history.replaceState(null, '', window.location.pathname); }} />}
          {mode === 'forgot'   && <ForgotView onBack={() => setMode('login')} />}
          {mode === 'login' && (
            <>
              <div className="text-center mb-8">
                <Logo />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Team Member Sign In</h2>
                <p className="text-gray-600">Sign in to access your contractor's projects</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    className={inputCls}
                    placeholder="Enter your email"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    <button
                      type="button"
                      onClick={() => setMode('forgot')}
                      className="text-xs text-primary hover:text-primary-dark font-medium"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    required
                    className={inputCls}
                    placeholder="Enter your password"
                  />
                </div>

                {error && <div className="text-red-600 text-sm text-center">{error}</div>}

                <button type="submit" disabled={loading} className={btnCls}>
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-gray-100 space-y-2 text-center">
                <p className="text-sm text-gray-500">
                  Are you a contractor?{' '}
                  <Link to="/account" className="text-primary hover:text-primary-dark font-medium">Sign in here</Link>
                </p>
              </div>
            </>
          )}
        </div>

        {mode === 'login' && (
          <p className="text-center text-xs text-gray-400 mt-6">
            Don't have an account? You need an invite link from your contractor.
          </p>
        )}
      </div>
    </div>
  );
};

export default TeamMemberLogin;
